import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../Context/AuthContext';
import demosApi from '../../utils/demos.api';

const DemoRequestPage = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();

  const [catalog, setCatalog] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filterApp, setFilterApp] = useState('All');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Admins never go through the request flow
    if (user?.role === 'admin') {
      navigate('/dashboard/demos');
      return;
    }
    // If user already has a pending/approved request, send them to the dashboard
    if (user?.access_status && user.access_status !== 'none') {
      navigate('/dashboard/demos');
      return;
    }
    demosApi.getCatalog()
      .then(({ data }) => {
        const apps = Array.isArray(data) ? data : [];
        setCatalog(apps);
      })
      .catch(() => setError('Failed to load demos. Please refresh and try again.'))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const appNames = ['All', ...catalog.map((g) => g.app)];

  const visibleDemos = catalog
    .filter((g) => filterApp === 'All' || g.app === filterApp)
    .flatMap((g) => g.demos.map((d) => ({ ...d, app: g.app })));

  const toggleSelect = (demoId) => {
    setSelectedIds((prev) =>
      prev.includes(demoId) ? prev.filter((id) => id !== demoId) : [...prev, demoId]
    );
  };

  const handleSubmit = async () => {
    if (selectedIds.length === 0) return;
    setSubmitting(true);
    try {
      await demosApi.requestAccess(selectedIds);
      // Optimistically update access_status so routing reacts immediately
      updateUser({ access_status: 'pending' });
      navigate('/dashboard/demos');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to submit request. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ maxWidth: 900, mx: 'auto', mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Welcome{user?.name ? `, ${user.name}` : ''}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Select the demos you'd like to access. Our team will review your request.
        </Typography>
      </Box>

      {error && (
        <Box sx={{ maxWidth: 900, mx: 'auto', mb: 2 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {/* App filter */}
      {appNames.length > 1 && (
        <Box sx={{ maxWidth: 900, mx: 'auto', mb: 3 }}>
          <ToggleButtonGroup
            value={filterApp}
            exclusive
            onChange={(_, val) => val && setFilterApp(val)}
            size="small"
          >
            {appNames.map((name) => (
              <ToggleButton key={name} value={name}>
                {name}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Demo grid */}
      <Box
        sx={{
          maxWidth: 900,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
          gap: 2,
          mb: 4
        }}
      >
        {visibleDemos.map((demo) => {
          const selected = selectedIds.includes(demo.id);
          return (
            <Card
              key={`${demo.app}-${demo.id}`}
              variant="outlined"
              sx={{
                border: selected ? '2px solid' : '1px solid',
                borderColor: selected ? 'primary.main' : 'divider',
                bgcolor: selected ? 'primary.50' : 'background.paper',
                transition: 'all 0.15s ease'
              }}
            >
              <CardActionArea onClick={() => toggleSelect(demo.id)} sx={{ p: 0 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Chip label={demo.app} size="small" variant="outlined" />
                    {selected
                      ? <CheckCircleIcon color="primary" />
                      : <RadioButtonUncheckedIcon sx={{ color: 'text.disabled' }} />
                    }
                  </Box>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    {demo.title}
                  </Typography>
                  {demo.description && (
                    <Typography variant="caption" color="text.secondary">
                      {demo.description}
                    </Typography>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>

      {/* Footer action bar */}
      <Box
        sx={{
          maxWidth: 900,
          mx: 'auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 2,
          position: 'sticky',
          bottom: 16
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {selectedIds.length === 0
            ? 'Select at least one demo to continue'
            : `${selectedIds.length} demo${selectedIds.length > 1 ? 's' : ''} selected`}
        </Typography>
        <Button
          variant="contained"
          size="large"
          disabled={selectedIds.length === 0 || submitting}
          onClick={handleSubmit}
        >
          {submitting ? 'Submitting...' : 'Request Access →'}
        </Button>
      </Box>
    </Box>
  );
};

export default DemoRequestPage;
